import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import cn from 'classnames'
import FocusLockUI from 'react-focus-lock'

import styles from './FocusTrap.module.scss'
import handleArrowKey from './helpers/handleArrowKey'
import handleClickOutside from './helpers/handleClickOutside'
import { FocusTrapProps } from './types'

const FocusTrap: React.FC<FocusTrapProps> = forwardRef(
  (
    {
      children,
      onEscape,
      className,
      isArrowKeyNavigationActivated,
      focusLockOverflow = 'auto',
      wrapperClassName,
      name,
      isActive = true,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null)

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => innerRef.current)

    useEffect(() => {
      if (isActive) {
        const handleKeyDown = ({ key }: KeyboardEvent): void => {
          if (key === 'Escape') {
            return onEscape()
          }

          return handleArrowKey(
            { current: innerRef.current, isArrowKeyNavigationActivated, key },
            name,
          )
        }

        document.addEventListener('keydown', handleKeyDown, true)

        return () => {
          document.removeEventListener('keydown', handleKeyDown, true)
        }
      }
      return undefined
    }, [])

    useEffect(() => {
      const clickOutside = (event: MouseEvent): void =>
        handleClickOutside({ current: innerRef.current, event, onEscape }, name)

      document.addEventListener('mousedown', clickOutside)
      return () => {
        document.removeEventListener('mousedown', clickOutside)
      }
    }, [])

    return (
      <FocusLockUI
        returnFocus
        className={cn(
          styles.container,
          focusLockOverflow === 'unset' && styles.container_overflowUnset,
          wrapperClassName,
        )}
        disabled={!isActive}
      >
        <div
          role="dialog"
          aria-modal="true"
          data-modal-name={name}
          className={className}
          ref={innerRef}
          {...props}
        >
          {children}
        </div>
      </FocusLockUI>
    )
  },
)

export default FocusTrap
